import { Trip, TripSearch } from "@fairfleet/geotab";
import type { O } from "ts-toolbelt";
import { RESULTS_LIMIT_TRIP } from "@/constants/resultsLimits";
import { chunkArray } from "@/utilities/chunkArray";

export type TripSearchTerm = O.Partial<TripSearch, "deep">;

/** Just the part of the Geotab client this needs, so it can be tested without one. */
export type GetTrips = (
  search: TripSearchTerm & { fromDate: Date; toDate: Date },
  resultsLimit: number,
) => Promise<Trip[]>;

/**
 * Trips per request.
 *
 * Geotab returns a date range oldest first and silently truncates it here, so a full page means
 * there is more to come rather than that the window is covered.
 */
export const TRIPS_PER_PAGE = RESULTS_LIMIT_TRIP;

/**
 * Searches started at a time.
 *
 * One search covers one vehicle or one driver, so a fleet-wide range is one search per vehicle.
 * Starting them all at once opens as many paging loops as there are vehicles and stops the tab
 * responding; batches run one after another.
 */
export const SEARCHES_PER_BATCH = 50;

export interface FetchTripsOptions {
  fromDate: Date;
  toDate: Date;
  searches: TripSearchTerm[];
  signal?: AbortSignal;
  /** Overridable so a test does not have to produce a full page of trips. */
  pageSize?: number;
  batchSize?: number;
  /** Called before the first batch and after each one with the searches done so far. */
  onProgress?: (done: number, total: number) => void;
  /** Called after each batch with every trip collected so far, so a page can show them early. */
  onBatch?: (collected: readonly Trip[]) => void;
}

/**
 * Every trip the given searches match, across the whole window.
 *
 * A single `Get` is not enough: Geotab caps the result and returns the *oldest* part of the range,
 * so a wider period silently loses the newest trips. Whenever a page comes back full, the search
 * continues just after the newest trip it returned.
 */
export async function fetchTripsComplete(
  get: GetTrips,
  options: FetchTripsOptions,
): Promise<Trip[]> {
  const { fromDate, toDate, searches, signal } = options;
  const pageSize = options.pageSize ?? TRIPS_PER_PAGE;
  const batchSize = options.batchSize ?? SEARCHES_PER_BATCH;

  const collected: Trip[] = [];
  let done = 0;
  options.onProgress?.(done, searches.length);

  for (const batch of chunkArray(searches, batchSize)) {
    signal?.throwIfAborted();

    const pages = await Promise.all(
      batch.map(search => fetchOne(get, search, fromDate, toDate, pageSize, signal)),
    );

    for (const page of pages) {
      // Pushed one at a time rather than spread: a fleet-sized month is more arguments than a
      // function call can take.
      for (const trip of page) {
        collected.push(trip);
      }
    }

    done += batch.length;
    options.onBatch?.(collected);
    options.onProgress?.(done, searches.length);
  }

  return collected;
}

async function fetchOne(
  get: GetTrips,
  search: TripSearchTerm,
  fromDate: Date,
  toDate: Date,
  pageSize: number,
  signal?: AbortSignal,
): Promise<Trip[]> {
  const trips: Trip[] = [];
  let pageFrom = fromDate;

  for (;;) {
    signal?.throwIfAborted();

    const page = await get({ ...search, fromDate: pageFrom, toDate }, pageSize);

    if (!Array.isArray(page)) {
      throw new Error(`Unexpected Trip response: expected an array, got ${String(page)}`);
    }

    for (const trip of page) {
      trips.push(trip);
    }

    if (page.length < pageSize) {
      return trips;
    }

    const newest = newestStart(page);
    if (newest === null) {
      throw new Error("Cannot page trips: a full page carried no usable start time");
    }

    // One search covers one vehicle or driver, whose trips do not overlap, so the cursor moves
    // by at least a millisecond each time. Trips sharing that exact millisecond beyond a full
    // page are the one case this cannot recover -- fifty thousand trips of one vehicle inside one
    // millisecond does not occur. The guard below is for a database that answers with trips older
    // than the range it was asked for.
    const next = new Date(newest + 1);
    if (next.getTime() <= pageFrom.getTime()) {
      throw new Error("Cannot page trips: the newest trip of a full page is not after its start");
    }
    if (next.getTime() > toDate.getTime()) {
      return trips;
    }

    pageFrom = next;
  }
}

function newestStart(page: Trip[]): number | null {
  let newest = Number.NEGATIVE_INFINITY;

  for (const trip of page) {
    const start = new Date(trip.start).getTime();
    if (Number.isFinite(start)) {
      newest = Math.max(newest, start);
    }
  }

  return Number.isFinite(newest) ? newest : null;
}
